import { motion } from "framer-motion";
import { FOUNDER, VALUES, AWARDS, PRESS, MANIFESTO } from "@/data/content";
import CTASection from "@/components/sections/CTASection";
import TrustBadges from "@/components/sections/TrustBadges";
import { Award } from "lucide-react";

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="pt-32 sm:pt-40">
      <section className="px-5 sm:px-6 lg:px-12 pb-12 sm:pb-16 lg:pb-20">
        <div className="max-w-[1400px] mx-auto">
          <p className="overline">About</p>
          <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[9rem] leading-[0.92] tracking-tight mt-5 sm:mt-6 max-w-[14ch]">
            A studio,
            <br />
            <span className="italic text-aurora">not an agency.</span>
          </h1>
        </div>
      </section>

      {/* Positioning hammer: pull quote + stats */}
      <section
        data-testid="about-pullquote"
        className="px-5 sm:px-6 lg:px-12 pb-16 sm:pb-24 lg:pb-32 border-t border-neutral-200 pt-12 sm:pt-20"
      >
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <p className="overline">The short version</p>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8 }}
              className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-5 sm:mt-6 text-neutral-900"
            >
              <span className="font-serif italic text-5xl sm:text-7xl text-gold leading-none mr-2 align-[-0.15em]">
                &ldquo;
              </span>
              {FOUNDER.pullQuote}
            </motion.p>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 grid grid-cols-3 gap-4 sm:gap-6 border-l border-neutral-200 pl-6 sm:pl-10">
            {FOUNDER.stats.map((s) => (
              <div key={`pq-${s.label}`}>
                <div className="font-serif italic text-3xl sm:text-4xl lg:text-5xl text-gold leading-none">
                  {s.value}
                </div>
                <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-neutral-500 mt-3 leading-tight">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 sm:px-6 lg:px-12 pb-16 sm:pb-24 lg:pb-32">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div
                data-testid="founder-image"
                className="w-full aspect-[4/5] border border-gold/30 bg-gradient-to-br from-ink-surface via-ink to-black flex flex-col items-center justify-center relative overflow-hidden"
              >
                <div
                  aria-hidden
                  className="aurora-orb aurora-orb-amber orb-anim-a absolute"
                  style={{ width: 360, height: 360, opacity: 0.18 }}
                />
                <div className="relative text-center px-6">
                  <p className="font-serif text-6xl sm:text-7xl text-neutral-900 leading-none">
                    GoldKey
                  </p>
                  <p className="font-serif italic text-5xl sm:text-6xl text-gold leading-none mt-3">
                    Studio.
                  </p>
                  <p className="overline mt-8">Boston · Worldwide</p>
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white border border-gold/40 p-5 max-w-[260px]">
                <p className="font-serif italic text-xl text-gold">
                  {FOUNDER.name}
                </p>
                <p className="text-xs uppercase tracking-[0.18em] text-neutral-600 mt-1">
                  {FOUNDER.role}
                </p>
              </div>
            </div>

            {FOUNDER.credentials && (
              <ul className="mt-12 sm:mt-16 space-y-3 border-t border-neutral-200 pt-6 sm:pt-8">
                {FOUNDER.credentials.map((c) => (
                  <li
                    key={c}
                    className="text-sm text-neutral-600 flex gap-3 items-start"
                  >
                    <span className="text-gold mt-[10px] block w-3 h-px bg-gold flex-none"></span>
                    {c}
                  </li>
                ))}
              </ul>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-6 lg:col-start-7"
          >
            <p className="overline">Why this studio exists</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.05] mt-5 sm:mt-6 mb-8 sm:mb-10">
              We designed the studio
              <br />
              <span className="italic text-aurora">we wished we could hire.</span>
            </h2>
            {FOUNDER.bio.map((p, i) => (
              <p
                key={`bio-${i}`}
                className={`text-base sm:text-lg text-neutral-700 leading-relaxed mb-5 sm:mb-6 ${
                  i === 0 ? "first-letter:font-serif first-letter:italic first-letter:text-6xl sm:first-letter:text-7xl first-letter:float-left first-letter:mr-3 first-letter:leading-none first-letter:text-gold" : ""
                }`}
              >
                {p}
              </p>
            ))}

            <div className="grid grid-cols-3 gap-4 sm:gap-6 mt-8 sm:mt-10 pt-8 sm:pt-10 border-t border-neutral-200">
              {FOUNDER.stats.map((s) => (
                <div key={s.label}>
                  <div className="font-serif italic text-3xl sm:text-4xl text-gold leading-none">
                    {s.value}
                  </div>
                  <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-neutral-500 mt-2 sm:mt-3">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="px-5 sm:px-6 lg:px-12 py-16 sm:py-24 lg:py-32 border-y border-neutral-200/70">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 sm:gap-10 mb-10 sm:mb-16">
            <div className="md:col-span-5">
              <p className="overline">Operating principles</p>
              <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05] mt-5 sm:mt-6">
                How <span className="italic text-aurora">this works.</span>
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-neutral-100">
            {VALUES.map((v, i) => (
              <div
                key={v.title}
                className="bg-white p-6 sm:p-10 lg:p-14"
                data-testid={`value-${i}`}
              >
                <span className="font-serif italic text-xl sm:text-2xl text-gold">
                  0{i + 1}
                </span>
                <h3 className="font-serif text-2xl sm:text-3xl mt-4 sm:mt-6 mb-3 sm:mb-4">{v.title}</h3>
                <p className="text-neutral-700 leading-relaxed text-sm sm:text-base">
                  {v.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TrustBadges />

      {/* Awards & Press */}
      <section
        data-testid="awards-section"
        className="px-5 sm:px-6 lg:px-12 py-16 sm:py-24 lg:py-32"
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 sm:gap-10 mb-10 sm:mb-16">
            <div className="md:col-span-5">
              <p className="overline">Awards and recognition</p>
              <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05] mt-5 sm:mt-6">
                The work,
                <br />
                <span className="italic text-aurora">noticed.</span>
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 flex items-end">
              <p className="text-base sm:text-lg text-neutral-700 leading-relaxed">
                I do not chase awards, but when the work is right, the right
                people notice. Selected recognition from the past three years.
              </p>
            </div>
          </div>

          <div className="border-t border-neutral-200">
            {AWARDS.map((a, i) => (
              <motion.div
                key={`${a.year}-${a.title}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                className="grid grid-cols-12 gap-4 sm:gap-6 py-5 sm:py-6 border-b border-neutral-200 group hover:bg-white transition-colors px-2 -mx-2"
                data-testid={`award-${i}`}
              >
                <div className="col-span-3 sm:col-span-2 font-serif italic text-2xl sm:text-3xl text-gold leading-none flex items-center">
                  {a.year}
                </div>
                <div className="col-span-6 sm:col-span-7 flex items-center text-neutral-900 text-sm sm:text-base">
                  <Award
                    size={14}
                    strokeWidth={1.5}
                    className="text-gold mr-3 sm:mr-4 flex-none opacity-60 group-hover:opacity-100 transition-opacity"
                  />
                  {a.title}
                </div>
                <div className="col-span-3 flex items-center justify-end text-neutral-500 text-xs sm:text-sm italic">
                  {a.project}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-14 sm:mt-20">
            <p className="overline mb-6 sm:mb-8">As featured in</p>
            <div className="flex flex-wrap gap-x-8 sm:gap-x-12 gap-y-4 sm:gap-y-5">
              {PRESS.map((p) => (
                <span
                  key={p}
                  className="font-serif italic text-xl sm:text-2xl text-neutral-400 hover:text-gold transition-colors tracking-wide"
                  data-testid={`press-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {p}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Manifesto */}
      <section
        data-testid="manifesto-section"
        className="px-5 sm:px-6 lg:px-12 py-16 sm:py-24 lg:py-32 border-y border-neutral-200/70 bg-[#FAFAFA]"
      >
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="overline">{MANIFESTO.eyebrow}</p>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1] mt-5 sm:mt-6">
              <span className="italic text-aurora">{MANIFESTO.title}</span>
            </h2>
            <p className="text-neutral-600 text-base sm:text-lg leading-relaxed mt-6 sm:mt-8 max-w-md">
              The fastest way to know if GoldKey Studio is the right fit is
              to read what we refuse to do. If any of these feels like a
              dealbreaker, we are probably not your studio. That is fine.
            </p>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <ol className="space-y-0">
              {MANIFESTO.items.map((item, i) => (
                <motion.li
                  key={`manifesto-item-${i}`}
                  initial={{ opacity: 0, x: 12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.06 }}
                  className="flex gap-4 sm:gap-6 py-5 sm:py-6 border-t border-neutral-200 last:border-b text-base sm:text-lg text-neutral-700 leading-relaxed"
                  data-testid={`manifesto-${i}`}
                >
                  <span className="font-serif italic text-xl sm:text-2xl text-gold leading-none flex-none w-8 sm:w-10">
                    0{i + 1}
                  </span>
                  <span>{item}</span>
                </motion.li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
